/**
 * Tipos del dominio — modelo de datos de Riga Traza.
 *
 * Estos tipos son la spec del contenido del store (en runtime serían filas de
 * tablas SQL cuando Eze conecte el back). Convención:
 *  - Los `as const` arrays exportan también el tipo unión derivado, para
 *    poder hacer `EstadoAnmat extends string` y al mismo tiempo iterarlos.
 *  - Las propiedades opcionales con `?` reflejan campos que pueden no estar
 *    presentes en records viejos (migraciones progresivas).
 */

// =============================================================================
// USUARIOS / SESIÓN
// =============================================================================

export const ROLES = ['Administrador', 'Gerente de Logística', 'Operador'] as const;
export type Rol = (typeof ROLES)[number];

/**
 * Mecanismo de autenticación que el usuario usó para entrar.
 *   - 'local': usuario/password tradicional (form clásico).
 *   - 'google': SSO con Google Workspace (paquete AB).
 *
 * Convive en el mismo modelo de usuario para que el audit trail pueda
 * dejar constancia del método de cada inicio de sesión sin tener tablas
 * separadas — un usuario puede haber usado ambos métodos a lo largo del
 * tiempo (ej: SSO normal pero password local en contingencia).
 */
export const PROVEEDORES_AUTH = ['local', 'google'] as const;
export type ProveedorAuth = (typeof PROVEEDORES_AUTH)[number];

export interface Usuario {
  id: string;
  username: string;
  nombre: string;
  iniciales: string;
  rol: Rol;
  /** Email corporativo. Requerido para login con Google (matcheo por hd+email). */
  email?: string;
  /** Legacy / fallback — la principal si no hay `ubicacionDefaultId`. */
  ubicacionId: string;
  /** GLN que se setea como activo al iniciar sesión. */
  ubicacionDefaultId?: string;
  /** Lista de GLNs en los que el usuario puede operar (multi-GLN). */
  ubicacionesIds?: string[];
  /** En el mock acepta cualquier password; en producción será un hash real. */
  passwordHash?: string;
  /**
   * PIN de firma electrónica (4-6 dígitos). Preparado pero NO usado todavía
   * por el `FirmaElectronicaModal` — sigue pidiendo password completo hasta
   * que se decida la migración. Cuando se active:
   *   - El modal cambia label "Contraseña" → "PIN de firma".
   *   - La validación en `reautenticar()` compara contra `usuario.pin`.
   *   - El Admin puede editar el PIN de cada usuario desde Seguridad → Usuarios.
   *   - Las operaciones GxP críticas siguen requiriendo PIN incluso si el
   *     login fue por Google SSO (Part 11 §11.200 "two distinct components").
   *
   * En producción NO se guarda en plano — hash con bcrypt/argon2 server-side.
   * El mock guarda el dígito en claro para que sea evidente que es DEMO.
   */
  pin?: string;
  /** Método de auth del último login. Solo informativo (la firma electrónica
   *  siempre usa password local / PIN — Part 11 §11.200 "two distinct components"). */
  proveedorAuth?: ProveedorAuth;
  /** URL de avatar — del Google profile si proveedorAuth === 'google'. */
  avatarUrl?: string;
}

export interface Session {
  usuarioId: string;
  /** ISO timestamp del login — usado como fingerprint de sesión. */
  loginAt: string;
  /** ISO timestamp del logout si la sesión cerró ordenadamente. */
  logoutAt?: string;
}

// =============================================================================
// UBICACIONES / TIPOS DE ACTOR
// =============================================================================

/** Catálogo SNT de tipos de actor. */
export const TIPOS_ACTOR = [
  'LABORATORIO',
  'DISTRIBUIDORA',
  'DROGUERIA',
  'FARMACIA',
  'ESTABLECIMIENTO ASISTENCIAL',
  'LAB. DE MEZCLA INTRAVENOSA',
  'OPERADOR LOGISTICO',
  'BOTIQUIN FARMACIA',
  'ESTABLECIMIENTO ESTATAL',
  'PAIS',
] as const;
export type TipoActor = (typeof TIPOS_ACTOR)[number];

export interface Ubicacion {
  id: string;
  nombre: string;
  gln: string;
  tipoActor: TipoActor;
  direccion?: string;
  cuit?: string;
  /** Tipo legible alternativo (planta, depósito, 3PL, ...). Solo cosmético. */
  tipo?: string;
  /**
   * `externa: true` → GLN de tercero (laboratorio/droguería externa) que nos
   * despacha producto. Aparece SOLO como origen de transferencias entrantes.
   * NUNCA debe ser operable por usuarios propios (filtrado en RBAC).
   */
  externa?: boolean;
}

// =============================================================================
// PRODUCTOS / CLIENTES
// =============================================================================

export interface Producto {
  gtin: string;
  nombre: string;
  presentacion: string;
}

// FIX B3 (calidad código): los tipos `Cliente` y `DomicilioEntrega` viven en
// `src/api/contracts/clientes.ts` como schemas zod (single source of truth).
// El código real consume esos tipos via `import type { Cliente } from
// '@/api/contracts/clientes'`. Antes acá había definiciones divergentes
// (razonSocial vs descripcion) que el código ignoraba.

// =============================================================================
// SERIES
// =============================================================================

export const TENENCIAS_SERIE = ['en_poder', 'en_transito', 'entregada', 'destruida'] as const;
export type TenenciaSerie = (typeof TENENCIAS_SERIE)[number];

export interface Serie {
  id: string;
  gtin: string;
  lote: string;
  /** Formato `MM/YYYY`. */
  vencimiento: string;
  tenencia: TenenciaSerie;
  /** GLN donde está actualmente. `null` cuando `tenencia=entregada|destruida`. */
  ubicacionActualId: string | null;
  /** ISO timestamp de creación. */
  createdAt: string;
  /**
   * `true` si la serie fue creada vía Ingreso/Alta manual (no por seed histórico).
   * Inmutable — afecta la derivación del estado de traza cuando no hay movimientos.
   */
  altaManual?: boolean;
  /** ID del lote de ingreso manual que la creó. Agrupa series del mismo batch. */
  ingresoId?: string;
  /**
   * Si está seteado, la serie está pickeada por un operador (transitorio, no
   * persiste entre sesiones — se barre periódicamente).
   */
  pickeoEn?: { pickeoId: string; usuarioId: string; ts: string };
}

// =============================================================================
// MOVIMIENTOS (timeline de cada serie)
// =============================================================================

export const TIPOS_EVENTO_MOV = [
  'alta',
  'alta_manual',
  'alta_externa',
  'despacho_transferencia',
  'despacho_venta',
  'despacho_destruccion',
  'recepcion',
  'cancelacion',
  'cancelacion_recepcion',
  'reversion_error',
] as const;
export type TipoEventoMov = (typeof TIPOS_EVENTO_MOV)[number];

export interface Movimiento {
  id: string;
  serieId: string;
  /** Operación asociada (puede ser null para movimientos `alta`/`alta_manual` sin op). */
  operacionId: string | null;
  tipoEvento: TipoEventoMov;
  /** ISO timestamp. */
  fecha: string;
  ubicacionId: string;
  usuarioId: string | null;
  codigoTransaccion: string | null;
  eventoAnmatId: number | null;
  detalle: string;
  /** Solo en altas manuales (v10): linkea al lote de ingreso. */
  ingresoId?: string;
}

/** Estado derivado del movimiento — viene de `op.anmat.estado`. NO persiste. */
export const ESTADOS_TRAZA_MOV = ['pendiente', 'enviada', 'error', 'cancelada'] as const;
export type EstadoTrazaMov = (typeof ESTADOS_TRAZA_MOV)[number];

/**
 * Estado de una traza (`traza_job.status` del back). `anulada` = la traza fue
 * exitosa y después se canceló su egreso: ya no cuenta como trazado.
 */
export const ESTADOS_TRAZA_JOB = ['pending', 'running', 'success', 'error', 'failed', 'anulada'] as const;
export type EstadoTrazaJob = (typeof ESTADOS_TRAZA_JOB)[number];

/** Estado derivado a nivel serie — calculado recorriendo sus movimientos. NO persiste. */
export const ESTADOS_TRAZA_SERIE = [
  'trazada',
  'pendiente',
  'solo_canceladas',
  'nunca_trazada',
] as const;
export type EstadoTrazaSerie = (typeof ESTADOS_TRAZA_SERIE)[number];

// =============================================================================
// OPERACIONES (despachos / recepciones)
// =============================================================================

export const TIPOS_OPERACION = [
  'despacho_transferencia',
  'despacho_venta',
  'despacho_destruccion',
  'recepcion',
] as const;
export type TipoOperacion = (typeof TIPOS_OPERACION)[number];

export const ESTADOS_ANMAT = [
  'no_informada',
  'en_cola',
  'informada',
  'confirmada',
  'error',
  'cancelada',
] as const;
export type EstadoAnmat = (typeof ESTADOS_ANMAT)[number];

export const TIPOS_MENSAJE_SNT = [
  'informarTransaccion',
  'informarDestruccion',
  'confirmarTransaccion',
] as const;
export type TipoMensajeSnt = (typeof TIPOS_MENSAJE_SNT)[number];

/** Severidad del error tipificado de ANMAT (catálogo `ANMAT_ERROR_CODES`). */
export type SeveridadError = 'bajo' | 'medio' | 'alto';

export interface AnmatError {
  code: string;
  msg: string;
  severidad: SeveridadError;
  ts: string;
}

export interface AnmatMessage {
  ts: string;
  tipo: 'request' | 'response';
  body: string;
}

export interface AnmatPayload {
  tipoMensaje: TipoMensajeSnt;
  estado: EstadoAnmat;
  intentos: number;
  codigoTransaccion: string | null;
  ultimoError?: AnmatError;
  mensajes: AnmatMessage[];
}

export interface Operacion {
  id: string;
  tipo: TipoOperacion;
  /** Ubicación origen — donde reside el operador que ejecuta. */
  ubicacionId: string;
  /**
   * Ubicación destino (solo para `despacho_transferencia` entre ubicaciones
   * internas). Permite que la vista Recepción liste pendientes filtrando por
   * `destinoUbicacionId === ubicacionActiva`.
   */
  destinoUbicacionId?: string;
  /** Solo en recepciones: ID de la operación de despacho que la originó. */
  opOrigenId?: string;
  /** Alias usado en algunos lugares — equivalente a `opOrigenId`. */
  operacionOrigenId?: string;
  remitoNumero: string | null;
  eventoAnmatId: number | null;
  /** ISO timestamp. */
  fecha: string;
  usuarioId: string;
  seriesIds: string[];
  /** Estado lifecycle interno (no ANMAT) — abierta / cerrada. */
  estado: 'abierta' | 'cerrada';
  anmat: AnmatPayload;
  pdfGeneradoAt?: string | null;
  /** Si fue cancelada, cuándo. */
  fechaCancelacion?: string;
}

// =============================================================================
// REMITOS (input del ERP)
// =============================================================================

// FIX B3 (calidad código): los tipos `Remito` y `RemitoItem` viven en
// `src/api/contracts/remitos.ts` como schemas zod (single source of truth).
// Acá mantenemos solo las constantes/uniones que `domain/` usa internamente
// (DESTINOS_REMITO, ESTADOS_REMITO_PLATAFORMA) — son shape estable que no
// depende del transporte.

export const ESTADOS_REMITO_PLATAFORMA = ['pendiente_trazar', 'trazado'] as const;
export type EstadoRemitoPlataforma = (typeof ESTADOS_REMITO_PLATAFORMA)[number];

export const DESTINOS_REMITO = [
  'ubicacion_interna',
  'cliente_externo',
  'destruccion',
] as const;
export type DestinoRemito = (typeof DESTINOS_REMITO)[number];

// =============================================================================
// CATÁLOGO ANMAT
// =============================================================================

export interface EventoAnmat {
  id: number;
  evento: string;
  informador: TipoActor;
  origen: TipoActor;
  /** `null` para eventos terminales (destrucción, robo, vencimiento sin destinatario). */
  destino: TipoActor | null;
}

// =============================================================================
// AUDIT TRAIL (GAMP 5 / 21 CFR Part 11)
// =============================================================================

export const AUDIT_CATEGORIAS = [
  'sesion',
  'operacion_critica',
  'lectura_sensible',
  'configuracion',
  'error',
] as const;
export type AuditCategoria = (typeof AUDIT_CATEGORIAS)[number];

/**
 * Input parcial del audit — lo que pasan los callers. Los campos snapshot
 * (timestamp, usuario, hashes) los completa `buildAuditEntry`.
 */
export interface AuditEntryInput {
  categoria: AuditCategoria;
  accion: string;
  entidadTipo?: string;
  entidadId?: string;
  valoresAntes?: unknown;
  valoresDespues?: unknown;
  razon?: string;
  /** Significado de la firma (21 CFR Part 11 §11.50(a)(2)). */
  significadoFirma?: string;
  /** Texto extra de contexto — separado de razon para no mezclar semánticas. */
  detalle?: string;
}

export interface AuditMetadatos {
  sesionId: string | null;
  userAgent: string | null;
}

export interface AuditEntry {
  id: string;
  /** ISO timestamp — generado por `buildAuditEntry`. */
  timestamp: string;
  usuarioId: string | null;
  /** Snapshot inmutable del nombre — sobrevive a cambios en master de usuarios. */
  usuarioNombreSnap: string;
  ubicacionId: string | null;
  rol: Rol | null;
  categoria: AuditCategoria;
  accion: string;
  entidadTipo: string | null;
  entidadId: string | null;
  valoresAntes: unknown;
  valoresDespues: unknown;
  razon: string | null;
  significadoFirma: string | null;
  detalle: string | null;
  metadatos: AuditMetadatos;
  /** Hash de la entry anterior — '00000000' al inicio de la cadena. */
  hashPrev: string;
  /** Hash de esta entry (incluye hashPrev en el payload). */
  hashCurrent: string;
}

export interface IntegridadAudit {
  ok: boolean;
  total: number;
  rotas: Array<{
    id: string;
    esperado: string;
    encontrado: string;
    prevEsperado: string;
    prevEncontrado: string;
  }>;
}

// =============================================================================
// API LOG (v13 — interacciones con sistemas externos)
// =============================================================================

export const API_SISTEMAS = ['erp', 'snt'] as const;
export type ApiSistema = (typeof API_SISTEMAS)[number];

export const HTTP_METODOS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type HttpMetodo = (typeof HTTP_METODOS)[number];

export interface ApiLogEntry {
  id: string;
  ts: string;
  sistema: ApiSistema;
  metodo: HttpMetodo;
  endpoint: string;
  status: number;
  /** Derivado de `status` — true si 2xx. */
  ok: boolean;
  duracionMs?: number;
  operacionId?: string;
  requestBody?: unknown;
  responseBody?: unknown;
  errorMsg?: string;
}

// =============================================================================
// NOTIFICACIONES
// =============================================================================

export interface Notificacion {
  id: string;
  ts: string;
  usuarioId: string | null;
  ubicacionId: string | null;
  tipo: 'success' | 'error' | 'info' | 'warning';
  titulo: string;
  mensaje: string;
  contexto: { view?: string; entidadId?: string } | null;
  leida: boolean;
}
